import { useEffect } from 'react';
import { GetServerSideProps } from 'next';

import { SitecorePageProps } from 'lib/page-props';
import { sitecorePagePropsFactory } from 'lib/page-props-factory';
import { isEmpty } from 'lodash-es';
import qs from 'qs';
import Layout from 'src/Layout';
// import NotFound from 'src/NotFound';
import { componentBuilder } from 'temp/componentBuilder';

import {
  getSpecialtySearch,
  ProviderSpecialtySearchParams,
} from '@/apis/provider-specialty-api/ProviderSpecialtyApi';
import { MIN_PHYSICIANS_COUNT } from '@/features/landing/constants';
import { ListingQueryParams } from '@/features/listing/constants';
import { ListingResponse } from '@/features/listing/listing.types';
import { isSpecialtySlug } from '@/features/listing/utils';
import {
  AZ_API_FORMAT,
  AZ_REQUEST_PARAMS,
  BASE_API_URL,
  FUZZY_PARAMS,
  LISTING_PAGE,
  LOCATIONS_URL,
  MARKET_LEVEL_URL_HEADER,
  PHYSICIANS_URL,
  PROFILE_PAGE,
} from '@/lib/constants';
import { isPhysicianSiteType } from '@/lib/utils';
import { HostApp } from '@/types/OasAppContextTypes';
import { OasBaseProps } from '@/types/OasBaseProps';
import { Pages, RouteSegments } from '@/types/Routing';
import {
  ComponentFields,
  ComponentPropsContext,
  ComponentRendering,
  SitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { handleEditorFastRefresh } from '@sitecore-jss/sitecore-jss-nextjs/utils';

interface PhysiciansPageProps extends SitecorePageProps {
  scSiteCookie?: string | undefined;
}

const SitecorePage = ({
  // notFound,
  componentProps,
  layoutData,
  headLinks,
  scSiteCookie,
}: PhysiciansPageProps): JSX.Element => {
  useEffect(() => {
    // Since Sitecore editors do not support Fast Refresh, need to refresh editor chromes after Fast Refresh finished
    handleEditorFastRefresh();
  }, []);

  // if (notFound) {
  //   return <NotFound />;
  // }

  const isEditing = layoutData.sitecore.context.pageEditing;

  return (
    <ComponentPropsContext value={componentProps}>
      <SitecoreContext
        componentFactory={componentBuilder.getComponentFactory({ isEditing })}
        layoutData={layoutData}
      >
        <Layout layoutData={layoutData} headLinks={headLinks} scSiteCookie={scSiteCookie} />
      </SitecoreContext>
    </ComponentPropsContext>
  );
};

// need to execute the server side retrieval in order to have the SC_SITE cookie value
// it makes the getStaticPaths and getStaticProps methods obsolete
export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req, query } = context;

  const props = await sitecorePagePropsFactory.create({
    ...context,
    params: context.req.headers,
  });

  const body = props.layoutData?.sitecore?.route?.placeholders
    .body[0] as ComponentRendering<ComponentFields> & {
    placeholders: {
      [key: string]: ComponentRendering<ComponentFields>[];
    };
  };

  const routeFields = body?.placeholders['col-1']?.[0].fields || ({} as OasBaseProps);

  if (props.notFound || routeFields?.notFound) {
    return {
      notFound: true,
    };
  }

  const marketLevelUrl = req.headers[MARKET_LEVEL_URL_HEADER] as string | undefined;
  const isLocation = marketLevelUrl?.includes(`/${RouteSegments.Locations}`);

  if (props?.layoutData?.sitecore?.route?.itemId && marketLevelUrl) {
    props.originalPath = marketLevelUrl as string;
  }

  const { program, coids } = routeFields;

  const isHomePage = req.headers['x-original-pathname'] === '/';

  const specialtyParams: ProviderSpecialtySearchParams = {
    facilityCoids: coids as string,
    market: null,
    program: (program as string) || 'web',
  };

  const specialties = await getSpecialtySearch(specialtyParams);

  // Handle specialty paths without /specialty/ prefix
  // Check if the path is in format /physicians/:specialtySlug instead of /physicians/specialty/:specialtySlug
  let enrichedQuery = query;
  const originalPathname = req.headers['x-original-pathname'] as string | undefined;
  if (originalPathname && !originalPathname.includes(`/${RouteSegments.Specialty}/`)) {
    const pathSegments = originalPathname.split('/').filter((segment) => segment);
    const physiciansIndex = pathSegments.indexOf(Pages.Physicians);

    if (physiciansIndex !== -1 && pathSegments.length > physiciansIndex + 1) {
      const potentialSpecialtySlug = pathSegments[physiciansIndex + 1];

      // Check if this is a specialty slug (not listing, profile, or other known routes)
      if (
        potentialSpecialtySlug !== Pages.Listing &&
        potentialSpecialtySlug !== Pages.Profile &&
        isSpecialtySlug(potentialSpecialtySlug, specialties)
      ) {
        // Enrich query params with specialty slug for frontend handling
        // Create a new query object to avoid mutating the original context query
        if (!query.specialty && !query.specialtyName) {
          enrichedQuery = {
            ...query,
            specialty: potentialSpecialtySlug,
          };
        }
      }
    }
  }

  if (isHomePage) {
    const urlTitleParam = query.urlTitle as string | undefined;

    const locationName = isLocation
      ? marketLevelUrl?.split(LOCATIONS_URL)[1].split(PHYSICIANS_URL)[0]
      : undefined;

    // Redirect to profile page if urlTitle is present in the query parameters
    if (urlTitleParam) {
      if (isLocation) {
        const locationPrefixedDestination = `${LOCATIONS_URL}${locationName}${PHYSICIANS_URL}${PROFILE_PAGE}/${urlTitleParam}`;

        return {
          redirect: {
            destination: locationPrefixedDestination,
            permanent: false,
            basePath: false,
          },
        };
      }

      return {
        redirect: {
          destination: `${PROFILE_PAGE}/${urlTitleParam}`,
          permanent: false,
        },
      };
    }

    // Redirect to listing page if any listing request query parameters are present in the query
    // as per 201862 story requirements
    const hasListingParams =
      Object.keys(enrichedQuery).some(
        (key) => ListingQueryParams[key as keyof typeof ListingQueryParams]
      ) || !isEmpty(enrichedQuery.keywordExact);

    if (hasListingParams) {
      const queryString =
        Object.keys(enrichedQuery).length > 0
          ? qs.stringify(enrichedQuery, { addQueryPrefix: true })
          : '';

      if (isLocation) {
        const locationPrefixedDestination = `${LOCATIONS_URL}${locationName}${PHYSICIANS_URL}${LISTING_PAGE}${queryString}`;
        return {
          redirect: {
            destination: locationPrefixedDestination,
            permanent: false,
            basePath: false,
          },
        };
      }

      return {
        redirect: {
          destination: `${LISTING_PAGE}${queryString}`,
          permanent: false,
        },
      };
    }

    const siteType = (routeFields.siteType ?? context.query.siteType) as string | undefined;
    const locationUrlTitle = (routeFields.locationUrlTitle ?? context.query.locationUrlTitle) as
      | string
      | undefined;
    const hostAppKey = context.query.hostApp
      ? (context.query.hostApp as string).toLowerCase()
      : undefined;

    const hostApp =
      typeof hostAppKey === 'string' && hostAppKey in HostApp
        ? HostApp[hostAppKey as keyof typeof HostApp]
        : HostApp.sitecore;

    if (siteType && isPhysicianSiteType(siteType)) {
      let physiciansCount = routeFields.physiciansCount as number | undefined;
      let physiciansUrlTitle = routeFields.physiciansUrlTitle as string | undefined;

      if (hostApp !== HostApp.sitecore) {
        const coids = context.query.facilityCoids as string | undefined;
        const program = context.query.program as string | undefined;
        if (coids && program && siteType) {
          const results = await getPhysicians({ coids, program, siteType, locationUrlTitle });
          physiciansCount = results.data?.totalProvidersCount;
          physiciansUrlTitle = results.data?.providerList?.[0]?.urlTitle;
        }
      }

      // Todo: extract to functions the logic for determining the redirect destination
      if (physiciansCount && physiciansCount === 1 && physiciansUrlTitle) {
        if (isLocation) {
          const locationPrefixedDestination = `${LOCATIONS_URL}${locationName}${PHYSICIANS_URL}${PROFILE_PAGE}/${physiciansUrlTitle}`;

          return {
            redirect: {
              destination: locationPrefixedDestination,
              permanent: false,
              basePath: false,
            },
          };
        }

        return {
          redirect: {
            destination: `${PROFILE_PAGE}/${physiciansUrlTitle}`,
            permanent: false,
          },
        };
      } else if (physiciansCount && physiciansCount < MIN_PHYSICIANS_COUNT) {
        const queryString =
          Object.keys(enrichedQuery).length > 0
            ? qs.stringify(enrichedQuery, { addQueryPrefix: true })
            : '';

        return {
          redirect: {
            destination: isLocation
              ? `${LOCATIONS_URL}${locationName}${PHYSICIANS_URL}${LISTING_PAGE}${queryString}`
              : `${LISTING_PAGE}${queryString}`,
            permanent: false,
            basePath: isLocation ? false : undefined,
          },
        };
      }
    }
  }

  let searchAddressData = null;

  if (enrichedQuery.zip) {
    const zip = enrichedQuery.zip as string | undefined;
    const stringParams = qs.stringify(
      {
        ...FUZZY_PARAMS,
        query: zip,
        ...AZ_REQUEST_PARAMS,
      },
      { addQueryPrefix: true, arrayFormat: 'comma' }
    );

    try {
      const res = await fetch(
        `https://atlas.microsoft.com/search/fuzzy/${AZ_API_FORMAT}${stringParams}`
      );

      if (!res.ok) {
        throw new Error(`Azure Maps API responded with status ${res.status}`);
      }
      searchAddressData = await res.json();
    } catch (error) {
      console.error('Failed to fetch address data from Azure Maps API:', error);
      searchAddressData = null;
    }
  }

  // Need this in order to validate if OAS is loaded under MHO
  const scSiteCookie = context.req.cookies['sc_site'] || null;

  // Todo: if we're in listing page we can call the listing API from here
  // in order to pass the data down to the listing component and avoid calling on the client side
  // this is especially useful for an url that has filters applied

  const extendedProps = {
    ...props,
    scSiteCookie,
    searchAddressData,
    specialties,
    keywordExact: `${enrichedQuery.keywordExact || ''}`,
  };

  return {
    props: extendedProps,
  };
};

const getPhysicians = async ({
  coids,
  program,
  siteType,
  locationUrlTitle,
}: {
  coids: string;
  program: string;
  siteType: string;
  locationUrlTitle: string | undefined;
}): Promise<ListingResponse> => {
  const payLoad = locationUrlTitle
    ? { coids, program, siteType, locationUrlTitle }
    : { coids, program, siteType };
  const response = await fetch(`${BASE_API_URL}/provider-search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payLoad),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch physicians');
  }
  return response.json();
};

export default SitecorePage;
